import { FC, PropsWithChildren } from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';

import { useKeyboardShortcut } from '@/hook/useKeyboardShortcut';

type ModalViewProps = PropsWithChildren<{
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    closeable?: boolean;
    bgContainerClassName?: string;
    containerClassName?: string;
    contentContainerClassName?: string;
    onClose: () => void;
    onSubmit?: () => void;
    onAnimationStart?: () => void;
}>;

export const ModalView: FC<ModalViewProps> = ({
    children,
    maxWidth = '2xl',
    closeable = true,
    onClose,
    onSubmit,
    onAnimationStart,
    bgContainerClassName,
    containerClassName,
    contentContainerClassName,
}) => {
    const close = () => {
        if (closeable) {
            onClose?.();
        }
    };

    const isActive = !!children;

    useKeyboardShortcut({
        shortcuts: {
            ['Enter']: onSubmit,
            ['Escape']: close,
        },
        isActive,
    });

    const maxWidthClass = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'sm:max-w-2xl',
    }[maxWidth];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={classNames(
                'fixed inset-0 flex overflow-y-auto px-4 py-6 sm:px-0 items-center z-50 transform transition-all bg-black/40 backdrop-blur-sm',
                containerClassName,
            )}
            onAnimationStart={onAnimationStart}>
            <div
                onClick={close}
                className={classNames('absolute inset-0', bgContainerClassName)}
            />
            <div
                className={classNames(
                    `mb-6 bg-neutral-light rounded-lg overflow-hidden shadow-extra-large transform transition-all sm:w-full sm:mx-auto`,
                    maxWidthClass,
                    contentContainerClassName,
                )}>
                {children}
            </div>
        </motion.div>
    );
};
